import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractSignPage() {
  const { contractId } = useParams();
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [signer, setSigner] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/contracts/${contractId}/public?token=${token}`);
        const data = await res.json();
        if (res.ok) { setContract(data.contract); setSigner(data.contract.client?.name || ''); }
        else setError(data.detail || 'Contrato no encontrado');
      } catch { setError('Error de conexión'); }
    })();
  }, [contractId, token]);

  const sign = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/contracts/${contractId}/accept`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, accepted, signer_name: signer }) });
      const data = await res.json();
      if (res.ok) setDone(data);
      else setError(data.detail || 'No se pudo firmar');
    } catch { setError('Error de conexión'); }
    setBusy(false);
  };

  if (error) return <main className="flex min-h-screen items-center justify-center bg-[#02040a] p-6"><p className="rounded-2xl bg-rose-500/10 px-6 py-4 text-rose-300" data-testid="contract-sign-error">{error}</p></main>;
  if (!contract) return <main className="flex min-h-screen items-center justify-center bg-[#02040a]"><p className="text-slate-400">Cargando contrato…</p></main>;

  const signed = done || contract.status === 'firmado';
  return (
    <main className="min-h-screen bg-[#02040a] px-4 pb-10 pt-28 text-slate-200" data-testid="contract-sign-page">
      <div className="mx-auto max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.35em] text-[#00d4ff]">Neuralia369 · Contrato de servicios</p>
        <h1 className="mt-2 text-2xl font-black text-white sm:text-3xl" data-testid="contract-number">{contract.contract_number}</h1>
        <p className="mt-1 text-sm text-slate-400">{contract.template_type} · {contract.fixed_service ? contract.fixed_service.name : `Plan ${contract.plan?.name}`} · {contract.created_at?.slice(0, 10)}</p>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <p className="text-sm"><b className="text-white">Cliente:</b> {contract.client?.name} {contract.client?.company ? `· ${contract.client.company}` : ''}</p>
          {contract.fixed_service ? (
            <p className="mt-1 text-sm"><b className="text-white">Servicio:</b> {contract.fixed_service.name} — ${contract.fixed_service.price_usd?.toFixed(2)} USD · {contract.fixed_service.delivery}</p>
          ) : (
            <p className="mt-1 text-sm"><b className="text-white">Plan:</b> {contract.plan?.name} {contract.plan?.price_usd_month ? `— $${contract.plan.price_usd_month}/mes · ${contract.plan.credits_month} créditos/mes` : '— solo recargas'}</p>
          )}
        </div>

        {contract.fixed_service ? (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="contract-fixed-service">
            <p className="text-sm font-black text-white">Anexo económico — forma de pago</p>
            <p className="mt-2 text-xs text-slate-400">{contract.fixed_service.description}</p>
            <div className="mt-3 space-y-1">
              {contract.fixed_service.payment_terms?.map((t) => (
                <div key={t.label} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-1.5 text-sm"><span>{t.label}<span className="block text-[11px] text-slate-500">{t.when}</span></span><span className="font-bold text-[#00d4ff]">${t.amount_usd?.toFixed(2)}</span></div>
              ))}
              <div className="flex items-center justify-between rounded-lg bg-[#00d4ff]/10 px-3 py-2 text-sm font-black"><span>Total · precio final todo incluido</span><span className="text-[#00d4ff]">${contract.fixed_service.price_usd?.toFixed(2)} USD</span></div>
            </div>
          </div>
        ) : (
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <p className="text-sm font-black text-white">Anexo de precios — servicios contratados</p>
          <div className="mt-2 space-y-1">
            {contract.services?.map((s) => (
              <div key={s.service_id} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-1.5 text-sm">
                <span>{s.name}</span><span className="font-bold text-[#00d4ff]">{s.price_credits} créditos · ${s.price_usd?.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-500">1 crédito = $0.10 USD · Precio final todo incluido, siempre desglosado en tu panel.</p>
        </div>
        )}

        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <p className="text-sm font-black text-white">Cláusulas</p>
          <div className="mt-2 max-h-72 space-y-2 overflow-y-auto pr-2 text-xs leading-relaxed text-slate-400" data-testid="contract-clauses">
            {contract.clauses?.map((c) => <p key={c.title}><b className="text-slate-200">{c.title}.</b> {c.text}</p>)}
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-[#00d4ff]/30 bg-[#00d4ff]/[0.05] p-5">
          {signed ? (
            <p className="text-center text-sm font-black text-emerald-300" data-testid="contract-signed-msg">✍️ Contrato firmado{done?.signed_at ? ` el ${done.signed_at.slice(0, 10)}` : ''}. Recibirás copia por email. ¡Bienvenido a Neuralia369!</p>
          ) : (
            <>
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1" data-testid="contract-accept-checkbox" />
                <span>He leído y acepto el contrato {contract.contract_number}, sus cláusulas y el anexo de precios (firma digital con fecha e IP).</span>
              </label>
              <input value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Nombre del firmante" className="mt-3 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white" data-testid="contract-signer-name" />
              <button onClick={sign} disabled={!accepted || busy} className="mt-3 w-full rounded-xl bg-[#00d4ff] py-3 text-sm font-black text-[#02040a] disabled:opacity-40" data-testid="contract-sign-btn">{busy ? 'Firmando…' : 'Firmar contrato'}</button>
            </>
          )}
          <a href={`${API}/contracts/${contractId}/pdf?token=${token}`} target="_blank" rel="noreferrer" className="mt-3 block text-center text-xs font-bold text-[#00d4ff] underline" data-testid="contract-download-pdf">Descargar PDF del contrato</a>
        </div>
      </div>
    </main>
  );
}
